import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { BUDGET_STATUS_ACTIVE, BUDGET_STATUS_DRAFT } from "components/Budget/helpers";
import EmptyCard from "components/Cards/EmptyCard";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { InsuranceIcon } from "../Icons/Icons";
import InsuranceSummaryCard from "./components/InsuranceSummaryCard";
import InsuranceSummaryDataTable from "./components/InsuranceSummaryDataTable";
import { InsuranceService } from "./InsuranceService";

export default function InsuranceSummaryWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);

  const CARD_GRID_KEY = "insurance_summary";
  const [workspaceMode, setWorkspaceMode] = useState(null);

  const { component: confirmDialogComponent } = useDialog();
  const { addAttachments } = useChatAssistantAttachments();
  const handleInsuranceServiceError = (err) => {
    console.warn(err);
    // enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: 'error' });
  };

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const { state: insuranceScenarios, actions: insuranceScenarioActions } = useCardController();
  const loadInsuranceScenarios = async () => {
    try {
      setIsLoading(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        crop_year: currentYear,
        is_active: true,
      };
      const _insuranceScenarios = (
        await UsersApiHelper.users_selectObject("InsuranceScenario", producerFilter, globalState)
      )?.sort((a, b) => a.name?.localeCompare(b.name));

      if (_insuranceScenarios?.length) {
        const _InsuranceService = new InsuranceService({
          insuranceScenarios: _insuranceScenarios,
          crop_year: currentYear,
          onError: handleInsuranceServiceError,
          apiConfig: globalState,
        });
        const _insuranceScenarioPopulated = await _InsuranceService.actions.loadInsuranceScenario();
        if (_insuranceScenarioPopulated?.length) {
          insuranceScenarioActions.replace(_insuranceScenarioPopulated);
        } else {
          insuranceScenarioActions.replace([]);
          setIsLoading(false);
        }

        const _loadPremiums = async () => {
          // Create a shallow copy to avoid mutating read-only array
          const scenarios = [..._insuranceScenarioPopulated];
          for (var ii = 0; ii < scenarios?.length; ii++) {
            const _currentInsuranceScenario = scenarios[ii];

            const calculatePremiumsResponseInsuranceScenario =
              await _InsuranceService.actions.calculatePremiums(_currentInsuranceScenario);
            scenarios[ii] = calculatePremiumsResponseInsuranceScenario;
          }

          scenarios.forEach((_scenario) => {
            UsersApiHelper.updateInsurancePlans({ InsurancePlans: _scenario?.totals?.insurancePlans }, globalState);
          });

          return scenarios;
        };

        if (_insuranceScenarioPopulated?.length) {
          _loadPremiums()
            .then((updatedScenarios) => {
              setIsLoading(false);
              insuranceScenarioActions.replace(updatedScenarios);
            })
            .catch((err) => {
              console.warn(err);
              enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
                preventDuplicate: true,
                variant: "error",
              });
            });
        } else {
          insuranceScenarioActions.replace([]);
          setIsLoading(false);
        }
      } else {
        insuranceScenarioActions.replace([]);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
      insuranceScenarioActions.replace([]);
      handleError(err);
    }
  };

  // --- --- ---
  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadInsuranceScenarios();
    }
  }, [currentYear, globalState?.producer_token]);

  useEffect(() => {
    addAttachments([{ name: "Insurance", content: insuranceScenarios }]);
  }, [insuranceScenarios]);

  const handleInsuranceScenarioCardOnChange = async (_newInsuranceScenario) => {
    try {
      setIsLoading(true);
      const updates = [];

      if (_newInsuranceScenario.is_active === false) {
        const remainingScenarios = insuranceScenarios.filter((s) => s.id !== _newInsuranceScenario.id);
        if (remainingScenarios.length === 1) {
          updates.push({ ...remainingScenarios[0], status: BUDGET_STATUS_ACTIVE });
        }
      }

      if (_newInsuranceScenario.status === BUDGET_STATUS_ACTIVE && _newInsuranceScenario.is_active !== false) {
        insuranceScenarios.forEach((s) => {
          if (s.id !== _newInsuranceScenario.id) {
            updates.push({ ...s, status: BUDGET_STATUS_DRAFT });
          }
        });
      }

      updates.push(_newInsuranceScenario);

      await UsersApiHelper.updateInsuranceScenarios({ InsuranceScenarios: updates }, globalState);

      await loadInsuranceScenarios();
    } catch (err) {
      handleError(err);
      setIsLoading(false);
    }
  };

  const handleAddNewClick = () => {
    navigate(`./0`);
  };

  return (
    <>
      <WorkspaceHeader
        title="Scenarios"
        category={{
          title: "Insurance",
          icon: <InsuranceIcon />,
        }}
      />

      <WorkspacePageHeader
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        viewMode={workspaceMode}
        onViewModeChange={setWorkspaceMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoading}
      />

      {workspaceMode === WORKSPACE_VIEW_CARD && (
        <Box className="cardContainer">
          {insuranceScenarios.map((currentScenario, index) => (
            <InsuranceSummaryCard
              key={index}
              insuranceScenario={currentScenario}
              onChange={handleInsuranceScenarioCardOnChange}
              isLoading={isLoading}
            />
          ))}
          {!insuranceScenarios?.length && (
            <EmptyCard
              text="No scenarios listed"
              secondaryText="Get started by adding a scenario"
              isLoading={isLoading}
            />
          )}
        </Box>
      )}

      {workspaceMode === WORKSPACE_VIEW_TABLE && (
        <InsuranceSummaryDataTable
          insuranceScenarios={insuranceScenarios}
          isLoading={isLoading}
          onChange={handleInsuranceScenarioCardOnChange}
        />
      )}

      <WorkspaceActionBar
        content={
          <Button variant="contained" onClick={handleAddNewClick}>
            <AddCircleOutline sx={{ mr: 1 }} />
            New Scenario
          </Button>
        }
      />

      {confirmDialogComponent}
    </>
  );
}
